class AlgoliaHit extends HTMLElement {
  constructor() {
    super();

    this.connectedCallback = this.connectedCallback.bind(this);
    this.getCatalogItemType = this.getCatalogItemType.bind(this);
    this.getColorCode = this.getColorCode.bind(this);
    this.getColors = this.getColors.bind(this);
    this.getGender = this.getGender.bind(this);
    this.getArticleNumber = this.getArticleNumber.bind(this);
    this.getParentSku = this.getParentSku.bind(this);
    this.getPartnerSku = this.getPartnerSku.bind(this);
    this.getSellingPrice = this.getSellingPrice.bind(this);
    this.getWidth = this.getWidth.bind(this);
    this.refreshListingAttributesForColor = this.refreshListingAttributesForColor.bind(this);
    this.render = this.render.bind(this);
    this.setListingAttributes = this.setListingAttributes.bind(this);
    this.slugifyColor = this.slugifyColor.bind(this);
    this.updateFeaturedVariant = this.updateFeaturedVariant.bind(this);
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    this.settings = {
      useAlgoliaFullPdpUrlParams: this.getAttribute('use-algolia-full-pdp-url-params').toLowerCase() === 'true',
      badgePosition: this.getAttribute('badge-position'),
      cardColorScheme: this.getAttribute('card-color-scheme'),
      cardStyle: this.getAttribute('card-style'),
      description: this.getAttribute('description'),
      enableZoomImage: this.getAttribute('enable-zoom-image'),
      extendHeight: this.getAttribute('extend-height').toLowerCase() === 'true',
      hideSubfieldsLabel: this.getAttribute('hide-subfields-label').toLowerCase() === 'true',
      hideWidthFromSubfields: this.getAttribute('hide-width-from-subfields').toLowerCase() === 'true',
      featuredImg: null,
      horizontalClass: this.getAttribute('horizontal-class').toLowerCase() === 'true',
      imgAspectRatio: this.getAttribute('img-aspect-ratio'),
      isUnstacked: this.getAttribute('is-unstacked').toLowerCase() === 'true',
      mediaCameraShy: this.getAttribute('media-camera-shy'),
      mixedStyles: this.getAttribute('mixed-styles'),
      onSale: this.getAttribute('on-sale'),
      openProductsInNewTab: this.getAttribute('open-products-in-new-tab').toLowerCase() === 'true',
      pageWidth: this.getAttribute('page-width'),
      productBadgeHorizontalPosition: this.getAttribute('product-badge-horizontal-position'),
      productBadgePaddingYCard: this.getAttribute('product-badge-padding-y-card'),
      productBadgePaddingXCard: this.getAttribute('product-badge-padding-x-card'),
      productBadgeTextSizeProductCard: this.getAttribute('product-badge-text-size-product-card'),
      productBadgeVerticalPosition: this.getAttribute('product-badge-vertical-position'),
      saleBadgeColorScheme: this.getAttribute('sale-badge-color-scheme'),
      saleBadgeRenderLocation: this.getAttribute('sale-badge-render-location'),
      showVendor: this.getAttribute('show-vendor').toLowerCase() === 'true',
      soldOut: this.getAttribute('sold-out'),
      soldOutBadgeColorScheme: this.getAttribute('sold-out-badge-color-scheme'),
      typeProductCardInfoDivider: this.getAttribute('type-product-card-info-divider'),
      typeProductCardNameAllCaps: this.getAttribute('type-product-card-name-all-caps').toLowerCase() === 'true',
      typeProductCardShowGender: this.getAttribute('type-product-card-show-gender').toLowerCase() === 'true',
      typeProductCardShowEllipsis: this.getAttribute('type_product_card_show_ellipsis').toLowerCase() === 'true',
      typeProductCardSubfieldShow: this.getAttribute('type-product-card-subfield-show').toLowerCase() === 'true',
      colorOptionCarouselDisplayType: this.getAttribute('color-option-carousel-display-type'),
      colorOptionCarouselShowSingle: this.getAttribute('color-option-carousel-show-single') === 'true',
      colorOptionCarouselLimitDisplay: this.getAttribute('color-option-carousel-limit-display') === 'true',
      showSecondaryImage: this.getAttribute('show-secondary-image') === 'true',
      vendor: this.getAttribute('vendor'),
    }
  }

  /**
   * Retrieves the catalog item type from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted catalog item type from the item's metadata.
   */
  getCatalogItemType(item) {
    const troveFacets = item?.meta['trove-facets'];
    const catalogItemType = troveFacets?.catalogItemType?.toString() || '';
    return catalogItemType;
  }

  /**
   * Retrieves the parent sku from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted parent sku from the item's metadata.
   */
  getColorCode(item) {
    const troveProduct = item?.meta['trove-product'];
    const colorCode = troveProduct?.color_code?.toString() || '';
    return colorCode;
  }

  /**
   * Retrieves an array of colors from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string[]} An array of colors extracted from the item's metadata.
   */
  getColors(item) {
    const troveProduct = item?.meta['trove-product'];
    const troveColors = troveProduct?.color_display?.toString() || '';
    const colors = troveColors.split('/');
    return colors;
  }

  /**
   * Retrieves the gender from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted gender from the item's metadata.
   */
  getGender(item) {
    const troveFacets = item?.meta['trove-facets'];
    const troveGender = troveFacets?.gender?.toString() || '';
    const gender = troveGender.replace("'s", '').replace('["', '').replace('"]', '');
    return gender;
  }

  /**
   * Retrieves the parent sku from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted parent sku from the item's metadata.
   */
  getParentSku(item) {
    const troveProduct = item?.meta['trove-product'];
    const parentSku = troveProduct?.parent_sku?.toString() || '';
    return parentSku;
  }

  /**
   * Retrieves the width from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted width from the item's metadata.
   */
  getWidth(item) {
    const troveFacets = item?.meta['trove-facets'];
    const troveWidth = troveFacets?.width?.toString() || '';
    const width = troveWidth.replace('["', '').replace('"]', '');
    return width;
  }

  /**
   * Retrieves the partner sku from the given item's metadata.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The extracted partner sku from the item's metadata.
   */
  getPartnerSku(item) {
    const troveProduct = item?.meta['trove-product'];
    const partnerSku = troveProduct?.partner_sku?.toString() || '';
    return partnerSku;
  }

  /**
   * Reduces a colour display value to the slug used in the article number.
   * Accents are folded to their base letter first so that this matches the
   * Liquid `handle` filter, which transliterates rather than discards: without
   * the fold, "Cafe Noir" spelled with an accent would lose the accented letter
   * entirely and the two render paths would disagree. Characters outside the
   * Latin alphabet are still dropped, so non-Latin colour names remain a known
   * gap that has to be closed upstream rather than here.
   * @param {string} colorDisplay - The colour display value.
   * @returns {string} The slug, or an empty string when nothing usable remains.
   */
  slugifyColor(colorDisplay) {
    return (colorDisplay || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  /**
   * Builds the article number by joining the parent sku to the colour slug.
   * @param {Object} item - The item object containing metadata.
   * @returns {string} The article number, or an empty string when either part is missing.
   */
  getArticleNumber(item) {
    const troveProduct = item?.meta['trove-product'];
    const parentSku = this.getParentSku(item);
    const colorSlug = this.slugifyColor(troveProduct?.color_display);
    if (!parentSku || !colorSlug) return '';
    return `${parentSku}-${colorSlug}`;
  }

  /**
   * Realigns the listing attributes after the shopper picks a different colour
   * on the card. The card then points at another colourway, so any attribute
   * describing the previous one is wrong.
   *
   * The article number is rebuilt from the new colour. The sku and the selling
   * price belong to the other colourway and cannot be derived from the colour
   * payload, so they are removed: for analytics an absent value is recoverable
   * and a wrong one is not.
   * @param {HTMLElement} cardWrapper - The card root element.
   * @param {string} colorDisplay - The newly selected colour display value.
   * @returns {void}
   */
  refreshListingAttributesForColor(cardWrapper, colorDisplay) {
    if (!cardWrapper.hasAttribute('data-item-position')) return;

    const parentSku = cardWrapper.getAttribute('data-model-number') || '';
    const colorSlug = this.slugifyColor(colorDisplay);

    if (parentSku && colorSlug) {
      cardWrapper.setAttribute('data-article-number', `${parentSku}-${colorSlug}`);
    } else {
      cardWrapper.removeAttribute('data-article-number');
    }

    cardWrapper.removeAttribute('data-sku');
    cardWrapper.removeAttribute('data-selling-price');
  }

  /**
   * Retrieves the lowest variant price for the item. Algolia stores prices in
   * major currency units, so no conversion is applied here. The result is
   * rounded to at most two decimal places and carries no currency symbol.
   * @param {Object} item - The item object containing price fields.
   * @returns {string} The price, or an empty string when no usable value exists.
   */
  getSellingPrice(item) {
    const rawPrice = item?.variants_min_price ?? item?.price;
    if (typeof rawPrice !== 'number' || !Number.isFinite(rawPrice) || rawPrice < 0) return '';
    return String(Math.round(rawPrice * 100) / 100);
  }

  /**
   * Adds product listing attributes to a card so downstream tooling can read
   * them from the DOM. An attribute is omitted when its value is unavailable.
   * The position attribute is only ever set on a listing card, so it doubles as
   * the marker that distinguishes these cards from product cards elsewhere on
   * the site. It is written all or nothing with the rest, so a card never holds
   * listing data without the marker that selects it.
   *
   * Position is the rank of the product among search results, counting products
   * only. Marketing tiles are injected into the grid afterwards and are not
   * counted, so it is not the visual slot number. It is also assigned before any
   * grouping filter runs, so on a store using that filter the surviving cards
   * keep their original ranks and the sequence has gaps.
   * @param {HTMLElement} element - The card root element.
   * @param {Object} item - The item object backing the card.
   * @returns {void}
   */
  setListingAttributes(element, item) {
    const position = item?.__position;

    /* All or nothing: without a position the card carries no marker, and a card
       holding listing data that nothing can select is worse than a plain one. */
    if (!Number.isInteger(position)) {
      console.warn('[listing-attributes] no usable position, attributes not written', {
        objectID: item?.objectID,
        handle: item?.handle,
      });
      return;
    }

    const attributes = {
      'data-item-position': String(position),
      'data-name': item?.title?.toString() || '',
      'data-selling-price': this.getSellingPrice(item),
      'data-model-number': this.getParentSku(item),
      'data-sku': this.getPartnerSku(item),
      'data-article-number': this.getArticleNumber(item),
    };

    /* The name and the price come from fields present on every record, so their
       absence means something upstream changed rather than a product lacking an
       optional value. The metafield backed attributes are legitimately optional
       and stay quiet. */
    const missing = ['data-name', 'data-selling-price'].filter((name) => attributes[name] === '');
    if (missing.length) {
      console.warn('[listing-attributes] expected fields missing from hit', {
        objectID: item?.objectID,
        handle: item?.handle,
        missing,
      });
    }

    Object.keys(attributes).forEach((name) => {
      const value = attributes[name];
      if (value !== '') element.setAttribute(name, value);
    });
  }

  createCardWrapper(item) {
    const div = document.createElement('div');
    div.classList.add('card-wrapper', 'product-card-wrapper');
    // setting a data attribute to the product card to allow access to the fields listed in the object
    // in other methods
    const dataProduct = {
      title: item.title,
      isLazyLoad: item.isLazyLoad,
      color_options: item.meta['trove-variants']?.color_options || {}
    };
    div.setAttribute('data-product', JSON.stringify(dataProduct));
    this.setListingAttributes(div, item);
    return div;
  }

  createCard({ featuredMedia }) {
    const div = document.createElement('div');
    div.classList.add(
      'card',
      `card--${this.settings.cardStyle}`,
    );
    if (featuredMedia) {
      div.classList.add('card--media');
    }
    if ( this.settings.cardStyle === 'card') div.classList.add(`color-${this.settings.cardColorScheme}`, 'gradient');
    if (this.settings.extendHeight) div.classList.add('card--extend-height');
    if (!featuredMedia && this.settings.cardStyle === 'card') div.classList.add('ratio');
    if (this.settings.horizontalClass) div.classList.add('card--horizontal');

    div.style = `--ratio-percent:${1 / this.settings.imgAspectRatio * 100}%;`;
    return div;
  }

  createCardInner({ featuredMedia}) {
    const div = document.createElement('div');
    div.classList.add(
      'card__inner',
    );
    if (this.settings.cardStyle === 'standard') div.classList.add(`color-${this.settings.cardColorScheme}`, 'gradient');
    if (featuredMedia || this.settings.cardStyle === 'card') div.classList.add('ratio');

    div.style = `--ratio-percent:${1 / this.settings.imgAspectRatio * 100}%;`;
    return div;
  }

  createMixedStylesBadgeStyles() {
    const style = document.createElement('style');
    const css = `
      .product__badge {
        position: absolute;
        padding: ${this.settings.productBadgePaddingYCard}px ${this.settings.productBadgePaddingXCard}px;
      }
    `;

    style.appendChild(document.createTextNode(css));
    return style;
  }

  createProductBadge(productBadgeContent) {
    const div = document.createElement('div');
    div.classList.add(
      'product__badge',
      this.settings.productBadgeTextSizeProductCard,
      this.settings.productBadgeVerticalPosition,
      this.settings.productBadgeHorizontalPosition
    );
    productBadgeContent.forEach(el => div.appendChild(el));
    return div;
  }

  createCardMediaImg({featuredMedia, item}) {
    const img = document.createElement('img');
    // the image urls are formatted differently between shopify's cdn and ours
    if (featuredMedia.includes('shopify')) {
      img.srcset = `
        ${featuredMedia}&width=165 165w,
        ${featuredMedia}&width=360 360w,
        ${featuredMedia}&width=533 533w,
        ${featuredMedia}&width=720 720w,
        ${featuredMedia}&width=940 940w,
        ${featuredMedia}&width=1066 1066w
      `;
      img.src = `${featuredMedia}&width=533`;
    } else {
      img.src = `${featuredMedia}`;
    }

    img.sizes = `(min-width: ${this.settings.pageWidth}px) ${(this.settings.pageWidth - 130) / 4}px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)`;
    img.alt = item.title;
    // directly assigning the onerror field was not working so witched to this
    img.setAttribute('onerror', `this.onerror=null;this.src='${this.settings.mediaCameraShy}'`)
    img.classList.add('motion-reduce');
    if (item.isLazyLoad) {
      img.loading = 'lazy';
    }
    img.width = 'auto';
    img.height = 'auto';
    return img;
  }

  /**
   * Updates the product variant info on the product card when a thumbnail is clicked
   * @param {*} cardWrapper - the product card
   * @param {*} evt - click event from thumbnail
   */
  updateFeaturedVariant(cardWrapper, evt) {
    evt.preventDefault();
    const item = JSON.parse(cardWrapper.getAttribute('data-product'));
    const newFeaturedImg = this.createCardMediaImg({ featuredMedia: evt.target.getAttribute('src'), item });
    const wrapper = cardWrapper.querySelector('.media--transparent');
    wrapper.replaceChild(newFeaturedImg, wrapper.firstChild);
    const altText = evt.target.getAttribute('alt');
    const altImg = wrapper.querySelector('img[data-alt-image]');
    if (altImg) {
      const newAltUrl = item.color_options[altText]?.alternateImageUrl;
      if (newAltUrl) altImg.src = newAltUrl;
    }
    const handle = cardWrapper.querySelector('.full-unstyled-link');
    const colorOption = item.color_options[altText];
    const thumbnails = cardWrapper.querySelectorAll('.algolia-hit-slider');
    Array.from(thumbnails).forEach((thumbnail) => {
      const img = thumbnail.firstElementChild;
      img.classList.remove('active');
    })
    handle.href = `/products/${colorOption.handle}`;
    evt.target.classList.add('active');
    this.updateColorText(altText, cardWrapper);
    this.updateVariantSizes(cardWrapper);
    this.refreshListingAttributesForColor(cardWrapper, altText);

    const quickAddOverlay = cardWrapper.querySelector('.algolia-quick-add');
    if (quickAddOverlay) {
      quickAddOverlay.dispatchEvent(new CustomEvent('colorchange', {
        detail: { handle: colorOption.handle, sizes: colorOption.sizes }
      }));
    }
  }

  /**
   * updates the product color text
   * @param {*} text 
   * @param {*} cardWrapper 
   */
  updateColorText(text, cardWrapper) {
    const displayedColorWrapper = cardWrapper.querySelector('.card__subfield-text');
    displayedColorWrapper.querySelector('span').innerText = text;
  }

  /**
   * Updates the displayed sizes based on selected variant
   * @param {*} cardWrapper 
   */
  updateVariantSizes(cardWrapper) {
    const item = JSON.parse(cardWrapper.getAttribute('data-product'));
    const colorOptions = item.color_options;
    const sizeDisplay = cardWrapper.querySelector('.algolia-hit-variant-sizes');
    const displayedColorWrapper = cardWrapper.querySelector('.card__subfield-text');
    const displayColor = displayedColorWrapper.querySelector('span').innerText;
    if (sizeDisplay) sizeDisplay.populateSizes(colorOptions[displayColor]?.sizes);
  }

  /**
   * temporarily hides the default product image, text, and sizes
   * to display the info of hovered variant
   * @param {*} cardWrapper 
   * @param {*} evt 
   */
  thumbnailMouseoverPreview(cardWrapper, evt) {
    evt.preventDefault();
    const item = JSON.parse(cardWrapper.getAttribute('data-product'));
    const newFeaturedImg = this.createCardMediaImg({ featuredMedia: evt.target.getAttribute('src'), item });
    const wrapper = cardWrapper.querySelector('.media--transparent');
    const altImg = wrapper.querySelector('img[data-alt-image]');
    if (altImg) {
      wrapper.classList.remove('media--hover-effect');
      altImg.style.display = 'none';
    }
    wrapper.firstChild.style.display = 'none';
    wrapper.appendChild(newFeaturedImg);
    const sizesDisplay = cardWrapper.querySelector('.algolia-hit-variant-sizes');
    const altText = evt.target.getAttribute('alt');
    if (sizesDisplay) {
      sizesDisplay.populateSizes(item.color_options[altText].sizes);
    }
    const displayedColorWrapper = cardWrapper.querySelector('.card__subfield-text');
    const currentColor = displayedColorWrapper.querySelector('span');
    currentColor.style.display = 'none';
    const previewColor = document.createElement('span');
    previewColor.innerText = altText;
    displayedColorWrapper.appendChild(previewColor);
  }

  /**
   * resets the variant to the existing one before thumbnail mouse hover
   * @param {*} cardWrapper 
   * @param {*} evt 
   */
  thumbnailMouseoverCleanup(cardWrapper, evt) {
    evt.preventDefault();
    const item = JSON.parse(cardWrapper.getAttribute('data-product'));
    const wrapper = cardWrapper.querySelector('.media--transparent');
    wrapper.removeChild(wrapper.lastChild);
    wrapper.firstChild.style.display = 'block';
    const altImg = wrapper.querySelector('img[data-alt-image]');
    if (altImg) {
      wrapper.classList.add('media--hover-effect');
      altImg.style.display = 'block';
    }
    const displayedColorWrapper = cardWrapper.querySelector('.card__subfield-text');
    const spans = displayedColorWrapper.querySelectorAll('span');
    Array.from(spans).forEach(span => {
      if (span.style.display !== 'none') displayedColorWrapper.removeChild(span);
      if (span.style.display === 'none') span.style.removeProperty('display');
    })
    const displayColor = displayedColorWrapper.querySelector('span').innerText;
    const sizesDisplay = cardWrapper.querySelector('.algolia-hit-variant-sizes');
    if (sizesDisplay) {
      sizesDisplay.populateSizes(item.color_options[displayColor].sizes);
    }
  }

  
  createCardMedia({ featuredMedia, isMixedStyles, mixedStylesCardProductContent, item }) {
    const div = document.createElement('div');
    div.classList.add('card__media');

    const mediaDiv = document.createElement('div');
    mediaDiv.classList.add('media--transparent', 'media');
    if(this.settings.enableZoomImage) mediaDiv.classList.add(this.settings.enableZoomImage);

    const img = this.createCardMediaImg({ featuredMedia, item });
    this.settings.featuredImg = img;
    if (isMixedStyles) {
      mixedStylesCardProductContent.forEach(el => mediaDiv.appendChild(el));
    } else {
      mediaDiv.appendChild(img);
    }
    div.appendChild(mediaDiv);
    return div;
  }

  createCardContent() {
    const div = document.createElement('div');
    div.classList.add('card__content');
    return div;
  }

  createCardInformation({featuredMedia, item, colors, productUrl}) {
    const div = document.createElement('div');
    div.classList.add('card__information');
    if (!featuredMedia && this.settings.cardStyle === 'standard') {
      const h3 = document.createElement('h3');
      h3.classList.add('card__heading', 'line-clamp-3', 'md:line-clamp-2', this.settings.typeProductCardNameAllCaps ? 'p-all-caps' : '');
      h3.id = `title-${item.id}`;

      const a = document.createElement('a');
      a.href = productUrl;
      a.id = `StandardCardNoMediaLink-${item.id}`;
      a.classList.add('full-unstyled-link');
      a.setAttribute('aria-labelledby', `StandardCardNoMediaLink-${item.id} NoMediaStandardBadge-${item.id}`);
      a.textContent = item.title;

      const subfieldDiv = document.createElement('div');
      subfieldDiv.classList.add('card__subfield');
      if (!this.settings.hideSubfieldLabel) {
        const p  = document.createElement('p');
        p.classList.add('card__color', 'card__subfield-text');
        const span = document.createElement('span');
        const text = colors.reduce((text, color) => {
          return `${text}${color}${index < colors.length - 1 ? '/' : ''}`;
        }, '');
        span.textContent = text;
        p.appendChild(span);
        subfieldDiv.appendChild(p);
      }
      h3.appendChild(a);
      h3.appendChild(subfieldDiv);
      div.appendChild(h3);
    }
    return div;
  }

  createCardBadge({ item, badgeSaleContent }) {
    const div = document.createElement('div');
    div.classList.add('card__badge');
    if (this.settings.badgePosition) {
      // this setting can be values like 'bottom left'
      this.settings.badgePosition.split(' ').forEach(position => div.classList.add(position));
    }
    if (!item.inventory_available) {
      const span = document.createElement('span');
      span.id = `NoMediaStandardBadge-${item.id}`;
      span.classList.add('badge', 'badge--bottom-left', `color-${this.settings.soldOutBadgeColorScheme}`);
      span.textContent = this.settings.soldOut;
      div.appendChild(span);
    }
    if (this.settings.saleBadgeRenderLocation === 'over_product_image') {
      badgeSaleContent.forEach(el => div.appendChild(el));
    }
    return div;
  }

  createCardInformation2({ featuredMedia, item, productUrl }) {
    const div = document.createElement('div');
    div.classList.add('card__information');
    const h3 = document.createElement('h3');
    h3.classList.add('card__heading', 'line-clamp-3', 'md:line-clamp-2');
    if ( this.settings.typeProductCardNameAllCaps ) h3.classList.add('p-all-caps');
    if (featuredMedia || this.settings.cardStyle === 'card') {
      h3.id = `title-${item.id}`;
    }
    const a = document.createElement('a');
    a.href = productUrl;
    a.id = `CardLink-${item.id}`;
    a.classList.add('full-unstyled-link', 'card__product-url');
    a.setAttribute('aria-labelledby', `CardLink-${item.id} Badge-${item.id}`);
    if (this.settings.openProductsInNewTab) {
      a.target = '_blank';
    }
    a.textContent = item.title;
    h3.appendChild(a);
    div.appendChild(h3);
    return div;
  }

  createCardSubfield({ gender, colors, width, item }) {
    const p = document.createElement('p');
    p.classList.add('card__subfield', 'card__subfield-text');
    if (this.settings.typeProductCardShowEllipsis) {
      p.classList.add('line-clamp-1');
    }

    if (!this.settings.hideSubfieldsLabel) {
      const divider = this.settings.typeProductCardInfoDivider || '|';
      const parts = [];

      if (this.settings.typeProductCardShowGender && gender) {
        parts.push(gender);
      }

      if (this.settings.isUnstacked) {
        if (item.option1) {
          parts.push(item.option1);
        }
        if (item.option2) {
          parts.push(item.option2);
        }
      }

      const colorText = colors.filter(c => c).join('/');
      const afterParts = [];

      if (width && !this.settings.hideWidthFromSubfields) {
        afterParts.push(width);
      }

      if (colorText) {
        if (parts.length > 0) {
          p.appendChild(document.createTextNode(parts.join(` ${divider} `) + ` ${divider} `));
        }
        const colorSpan = document.createElement('span');
        colorSpan.classList.add('card__color-text');
        colorSpan.textContent = colorText;
        p.appendChild(colorSpan);
        if (afterParts.length > 0) {
          p.appendChild(document.createTextNode(` ${divider} ` + afterParts.join(` ${divider} `)));
        }
      } else {
        p.textContent = [...parts, ...afterParts].join(` ${divider} `);
      }
    }

    return p;
  }

  createCardInformation3({ item, badgeSaleContent, priceContent }) {
    const div = document.createElement('div');
    if (this.settings.showVendor) {
      const span = document.createElement('span');
      span.classList.add('visually-hidden');
      span.textContent = this.settings.vendor;
      div.appendChild(span);

      const captionDiv = document.createElement('div');
      captionDiv.classList.add('caption-with-letter-spacing', 'light');
      captionDiv.textContent = item.vendor;
      div.appendChild(captionDiv);
    }

    const captionSpan = document.createElement('span');
    captionSpan.classList.add('caption-large', 'light');
    captionSpan.textContent = this.settings.description;
    div.appendChild(captionSpan);
    if (this.settings.saleBadgeRenderLocation === 'product_info') {
      badgeSaleContent.forEach(el => div.appendChild(el));
    }
    div.appendChild(priceContent);
    return div;
  }

  createCardBadge2({ item }) {
    const div = document.createElement('div');
    div.classList.add('card__badge');
    if (this.settings.badgePosition) {
      // this setting can be values like 'bottom left'
      this.settings.badgePosition.split(' ').forEach(position => div.classList.add(position));
    }
    if (!item.inventory_available) {
      const span = document.createElement('span');
      span.id = `Badge-${item.id}`;
      span.classList.add('badge', 'badge--bottom-left', `color-${this.settings.soldOutBadgeColorScheme}`);
      span.textContent = this.settings.soldOut;
      div.appendChild(span);
    }else if (item.compare_at_price > item.price && item.inventory_available) {
      const span = document.createElement('span');
      span.id = `Badge-${item.id}`;
      span.classList.add('badge', 'badge--bottom-left', `color-${this.settings.saleBadgeColorScheme}`);
      span.textContent = this.settings.onSale;
      div.appendChild(span);
    }

    return div;
  }

  /**
   * Renders the content.
   * @param {Object} instances - Instances object containing algolia instances.
   * @param {Object} item - The item object containing information about the hit.
   * @returns {string} The rendered content HTML.
   */
  render(instances, item) {
    /* Metadata. */
    const catalogItemType = this.getCatalogItemType(item);
    const colorCode = this.getColorCode(item);
    const colors = this.getColors(item);
    const gender = this.getGender(item);
    const parentSku = this.getParentSku(item);
    const width = this.getWidth(item);

    const featuredMedia = item.product_image || this.settings.mediaCameraShy;
    const isMixedStyles = catalogItemType.includes('OPEN_SKU');

    /* Elements. */
    const badgeSaleElement = this.querySelector('algolia-badge-sale');
    const mixedStylesCardProductElement = this.querySelector('algolia-mixed-styles-card-product');
    const priceElement = this.querySelector('algolia-price');
    const productBadgeElement = this.querySelector('algolia-product-badge');
    const customBadgesElement = this.querySelector('algolia-custom-badges');

    /* Content. */
    const badgeSaleContent = badgeSaleElement ? badgeSaleElement.render(item) : [];
    const mixedStylesCardProductContent = mixedStylesCardProductElement ? mixedStylesCardProductElement.render(colorCode, parentSku) : [];
    const priceContent = priceElement ? priceElement.render(item) : [];
    const productBadgeContent = productBadgeElement ? productBadgeElement.render(this.settings?.mixedStyles) : [];
    const customBadgesContent = customBadgesElement ? customBadgesElement?.render(item, isMixedStyles) : []

    /* Product url link */
    let  productUrl = instances.algoliaShopify.helpers.instantsearchLink(item);
    if (!this.settings.useAlgoliaFullPdpUrlParams) {
      productUrl = productUrl.split('?')[0];
    }

    const cardWrapper = this.createCardWrapper(item);

    // These are the top half of a product card's html
    const card = this.createCard({ featuredMedia});
    const cardInner = this.createCardInner({ featuredMedia});

    const cardMedia = this.createCardMedia({ featuredMedia, isMixedStyles, mixedStylesCardProductContent, item });
    const cardContent = this.createCardContent();
    const cardInformation = this.createCardInformation({ featuredMedia, item, colors, productUrl });

    // These are the bottom half of a product card's html
    const cardContent2 = this.createCardContent();
    const cardInformation2 = this.createCardInformation2({ featuredMedia, item, productUrl });
    const cardSubfield = this.createCardSubfield({ gender, colors, width, item });
    const cardInformation3 = this.createCardInformation3({ item, badgeSaleContent, priceContent });
    const cardBadge2 = this.createCardBadge2({ item });
    const cardBadge = this.createCardBadge({ item, badgeSaleContent})

    if (isMixedStyles) {
      const mixedStylesBadgeSyles = this.createMixedStylesBadgeStyles();
      const productBadge = this.createProductBadge(productBadgeContent);
      cardInner.appendChild(mixedStylesBadgeSyles);
      cardInner.appendChild(productBadge);
    }

    if (customBadgesContent) {
      const customBadgeStyles = document.createElement('style');
      const css = `
        .product__badge {
          position: ${this.settings.saleBadgeRenderLocation === 'product_info' ? 'unset' : 'absolute'};
          padding: ${this.settings.productBadgePaddingYCard}px ${this.settings.productBadgePaddingXCard}px;
        }
      `;
      customBadgeStyles.appendChild(document.createTextNode(css));
      cardInner.appendChild(customBadgeStyles);
      customBadgesContent.forEach(badge => {
        if (this.settings.saleBadgeRenderLocation === 'product_info') {
          badge.forEach(el => cardInformation3.appendChild(el));
        } else {
          badge.forEach(el => cardInner.appendChild(el));
        }
      });
    }

    cardInner.appendChild(cardMedia);
    cardContent.appendChild(cardInformation);
    cardContent.appendChild(cardBadge);
    cardInner.appendChild(cardContent);

    card.appendChild(cardInner);

    cardInformation2.appendChild(cardSubfield);
    cardInformation2.appendChild(cardInformation3);
    cardContent2.appendChild(cardInformation2);
    cardContent2.appendChild(cardBadge2);
    card.appendChild(cardContent2);

    const carousel = this.querySelector('algolia-hit-carousel');
    const sizeDisplay = this.querySelector('algolia-hit-variant-sizes');
    cardWrapper.updateFeaturedVariant = (img) => { this.updateFeaturedVariant(cardWrapper, img) };
    cardWrapper.thumbnailMouseoverPreview = (img) => { this.thumbnailMouseoverPreview(cardWrapper, img) };
    cardWrapper.thumbnailMouseoverCleanup = (img) => { this.thumbnailMouseoverCleanup(cardWrapper, img) };
    const colorOptions = item.meta['trove-variants']?.color_options;
    const minColorOptions = this.settings.colorOptionCarouselShowSingle ? 1 : 2;
    if(carousel && colorOptions && Object.keys(colorOptions).length >= minColorOptions && !isMixedStyles) {
      const colorSpan = cardSubfield.querySelector('.card__color-text');
      // we render the carousel/slider element here,
      // but dont call render on the actual slider-component until after
      // algolia finished mounting because we need rendered element sizes
      // and these values are not set until after algolia mounts
      const carouselEl = carousel.render(
        item.handle,
        item.meta['trove-variants']?.color_options,
        colorSpan?.textContent,
        cardWrapper.thumbnailMouseoverPreview,
        cardWrapper.thumbnailMouseoverCleanup,
        cardWrapper.updateFeaturedVariant,
        this.settings.mediaCameraShy,
        this.settings.colorOptionCarouselDisplayType,
        this.settings.colorOptionCarouselShowSingle,
        this.settings.colorOptionCarouselLimitDisplay
      );
      card.appendChild(carouselEl);
      
    }
    if (sizeDisplay) {
      const colorOptions = item.meta['trove-variants'].color_options;
      const itemColor = item.meta['trove-product'].color_display;
      const found = Array.from(Object.keys(colorOptions)).find(option => {
        return option === itemColor;
      });
      const sizes = colorOptions[found]?.sizes || [];
      // we render the carousel/slider element here,
      // but dont populate the sizes until after
      // algolia finished mounting because we need rendered element sizes
      // and these values are not set until after algolia mounts
      const sizesElement = sizeDisplay.render(sizes);
      card.appendChild(sizesElement);
    }

    if (this.settings.showSecondaryImage && colorOptions) {
      const initialColor = item.meta['trove-product']?.color_display;
      const alternateUrl = initialColor && colorOptions[initialColor]?.alternateImageUrl;
      if (alternateUrl) {
        const mediaDiv = cardMedia.querySelector('.media--transparent');
        if (mediaDiv) {
          const altImg = this.createCardMediaImg({ featuredMedia: alternateUrl, item });
          altImg.setAttribute('data-alt-image', 'true');
          mediaDiv.classList.add('media--hover-effect');
          mediaDiv.appendChild(altImg);
        }
      }
    }

    cardWrapper.appendChild(card);

    // Quick-add overlay – sits inside card__inner so CSS bottom:0 aligns it
    // to the bottom of the image area. z-index:2 keeps it above the
    // full-unstyled-link::after overlay (z-index:1). card__inner has
    // position:relative + z-index:auto so it does NOT create a stacking
    // context — our z-index competes in the cardWrapper context.
    const quickAddElement = this.querySelector('algolia-quick-add');
    if (quickAddElement && item.inventory_available && !isMixedStyles) {
      const attachQuickAdd = () => {
        const quickAddEl = quickAddElement.render(item);
        const cardInner = card.querySelector('.card__inner');
        (cardInner || cardWrapper).appendChild(quickAddEl);
      };
      // Guard against race condition: the Algolia app extension may fire
      // render before deferred theme scripts have registered the custom
      // element class. If so, wait for the upgrade then attach.
      if (typeof quickAddElement.render === 'function') {
        attachQuickAdd();
      } else {
        customElements.whenDefined('algolia-quick-add').then(attachQuickAdd);
      }
    }

    // const slider = this.querySelector('slider-component');
    // slider.render({slider, sliderItems: slider.querySelectorAll('li')});
    return cardWrapper;
  }
}

customElements.define("algolia-hit", AlgoliaHit);
